import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { EmbeddedWallet } from "@domain/types/EmbeddedWallet";
import type { IWeb3AuthGateway } from "@domain/ports/IWeb3AuthGateway";
import type { RootState } from "../index";

/**
 * Sync status for embedded wallet operations
 */
export type EmbeddedWalletSyncStatus = "idle" | "syncing" | "synced" | "error";

/**
 * Embedded Wallet State Interface
 */
export interface EmbeddedWalletState {
  /** List of embedded wallets (ETH, POLYGON, SOL) */
  wallets: EmbeddedWallet[];
  /** Current sync operation status */
  syncStatus: EmbeddedWalletSyncStatus;
  /** Timestamp of last successful sync */
  lastSyncAt: string | null;
  /** Error message from last failed operation */
  error: string | null;
}

/**
 * Initial State
 */
const initialState: EmbeddedWalletState = {
  wallets: [],
  syncStatus: "idle",
  lastSyncAt: null,
  error: null,
};

/**
 * Extra dependencies for embedded wallet thunks
 *
 * These are passed via the thunk middleware's extraArgument.
 * In production, web3AuthGateway is injected from React context.
 */
export interface EmbeddedWalletThunkExtra {
  web3AuthGateway: IWeb3AuthGateway;
}

/**
 * Async Thunk: Sync Embedded Wallets
 *
 * Authenticates with Web3Auth using Cognito ID token,
 * derives embedded wallets, and syncs them to the backend.
 *
 * This thunk should be dispatched after successful Cognito login.
 * It runs as a non-blocking background operation.
 *
 * AC: 9, 10, 11, 12, 13, 14, 15 - Wallet sync with graceful error handling
 *
 * @param idToken - Cognito ID token for Web3Auth authentication
 */
export const syncEmbeddedWallets = createAsyncThunk<
  EmbeddedWallet[],
  { idToken: string },
  { state: RootState; extra: EmbeddedWalletThunkExtra; rejectValue: string }
>(
  "embeddedWallet/sync",
  async ({ idToken }, { extra, rejectWithValue }) => {
    try {
      // 1. Authenticate with Web3Auth using Cognito token
      await extra.web3AuthGateway.authenticateWithCognito(idToken);

      // 2. Get embedded wallets from Web3Auth (derives multi-chain)
      const wallets = await extra.web3AuthGateway.getEmbeddedWallets();

      // 3. Sync to backend
      // TODO: Add backend API call to POST /api/v1/owner/embedded-wallets
      // For now, just return the derived wallets
      console.log("[syncEmbeddedWallets] Wallets derived:", wallets.length);

      return wallets;
    } catch (error) {
      // Log error but don't show to user (AC: 13, 14, 15)
      console.error("[syncEmbeddedWallets] Sync failed:", error);

      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Embedded Wallet Slice
 *
 * Manages state for Web3Auth embedded wallets (ETH, POLYGON, SOL).
 * Handles async sync operations with proper status tracking.
 */
const embeddedWalletSlice = createSlice({
  name: "embeddedWallet",
  initialState,
  reducers: {
    /**
     * Clear any error state
     */
    clearError: (state) => {
      state.error = null;
    },
    /**
     * Reset to initial state (useful for logout)
     */
    resetWalletState: () => initialState,
    /**
     * Manually set wallets (for testing or SSR)
     */
    setWallets: (state, action: PayloadAction<EmbeddedWallet[]>) => {
      state.wallets = action.payload;
      state.syncStatus = "synced";
      state.lastSyncAt = new Date().toISOString();
    },
    /**
     * Set sync status to syncing (used by hook when calling gateway directly)
     */
    setSyncPending: (state) => {
      state.syncStatus = "syncing";
      state.error = null;
    },
    /**
     * Set sync as failed with error message
     */
    setSyncFailed: (state, action: PayloadAction<string>) => {
      state.syncStatus = "error";
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Sync Embedded Wallets - Pending
      .addCase(syncEmbeddedWallets.pending, (state) => {
        state.syncStatus = "syncing";
        state.error = null;
      })
      // Sync Embedded Wallets - Fulfilled
      .addCase(syncEmbeddedWallets.fulfilled, (state, action) => {
        state.syncStatus = "synced";
        state.wallets = action.payload;
        state.lastSyncAt = new Date().toISOString();
        state.error = null;
      })
      // Sync Embedded Wallets - Rejected
      .addCase(syncEmbeddedWallets.rejected, (state, action) => {
        state.syncStatus = "error";
        state.error = action.payload || "Sync failed";
        // Keep existing wallets on error (graceful degradation - AC: 14)
      });
  },
});

/**
 * Actions
 */
export const { clearError, resetWalletState, setWallets, setSyncPending, setSyncFailed } =
  embeddedWalletSlice.actions;

/**
 * Selectors
 */
export const selectEmbeddedWallet = (state: RootState) => state.embeddedWallet;
export const selectWallets = (state: RootState) =>
  state.embeddedWallet.wallets;
export const selectSyncStatus = (state: RootState) =>
  state.embeddedWallet.syncStatus;
export const selectIsSyncing = (state: RootState) =>
  state.embeddedWallet.syncStatus === "syncing";
export const selectIsSynced = (state: RootState) =>
  state.embeddedWallet.syncStatus === "synced";
export const selectSyncError = (state: RootState) =>
  state.embeddedWallet.error;
export const selectLastSyncAt = (state: RootState) =>
  state.embeddedWallet.lastSyncAt;

/**
 * Reducer
 */
export default embeddedWalletSlice.reducer;
